// Single source of truth for the Replenishment ActivePersonaContext.
//
// Holds the known-good dev/test defaults (scenario run, ref + comparison
// policies, metric window), the per-page deviations from that base, and the
// experiment_id → product-facing policy name mapping. The useActivePersonaContext
// hook resolves URL params over these defaults; the policy-label helpers read
// POLICY_NAMES so every surface (chart, build-up, tables, cards) names the
// policies the same way.

// TODO(engineer): POLICY_IDS encodes tenant-specific experiment/policy identifiers
// (model + config combinations) as literal strings in feature code.
// Canonical experiment ids for the three policies — one place to update an id.
export const POLICY_IDS = {
  incumbent: 'rc_4-ltdelta_0-forecast_ETS-col_POINT-sltype_ALPHA_CYCLE-tip_SSCS-cr_HIST',
  keystoneForecast: 'rc_4-ltdelta_0-forecast_BFG-col_P50-sltype_ALPHA_CYCLE-tip_TRNQ-cr_HIST',
  keystoneOptimized: 'rc_2-ltdelta_-2-forecast_BFG-col_P50-sltype_ALPHA_CYCLE-tip_TRNQ-cr_OPT',
} as const;

export interface PersonaContextDefaults {
  scenarioRunId: string;
  runId: string;
  refExperimentId: string;
  // Comparison policies, in display order.
  experimentIds: string[];
  periodFrom: string;
  periodTo: string;
  // Chart display overrides (Value Opportunity page).
  keystoneOnlyOverride: boolean;
  overrideValue: number;
  chartMultiplier: number;
}

// Per-page deviations from DEFAULT_PERSONA, layered on by useActivePersonaContext.
export interface PersonaOverrides {
  sku?: string;
  site?: string;
  experimentIds?: string[];
}

// TODO(engineer): scenarioRunId/runId are hardcoded known-good dev/test defaults, not
// resolved per tenant. Follow-up: once there is a way to look up the active/latest
// scenario run per tenant (analogous to the forecasts feature's solutions-service
// resolution), replace these literals with that lookup.
// Shared base — scenario/run/ref/window are the same across all pages.
export const DEFAULT_PERSONA: PersonaContextDefaults = {
  scenarioRunId: 'z100-config-4k-sku-20260629-init-inv-0-backlog-0.2',
  runId: '2b093511-5759-429e-8c76-2b98fe32a87d',
  refExperimentId: POLICY_IDS.incumbent,
  experimentIds: [POLICY_IDS.keystoneForecast, POLICY_IDS.keystoneOptimized],
  periodFrom: '2024-07-29',
  periodTo: '2025-09-01',
  keystoneOnlyOverride: false,
  overrideValue: 1_100_000,
  chartMultiplier: 1,
};

// Per-page defaults. Value Opportunity compares both Keystone policies (→ three
// policies with the incumbent); Active Planning focuses on the single optimized
// policy; the SKU×Site view also carries its sku/site scope.
export const PAGE_PERSONA: Record<
  'valueOpportunity' | 'activePlanning' | 'productStockingLocations',
  PersonaOverrides
> = {
  valueOpportunity: {
    experimentIds: [POLICY_IDS.keystoneForecast, POLICY_IDS.keystoneOptimized],
  },
  activePlanning: {
    experimentIds: [POLICY_IDS.keystoneOptimized],
  },
  productStockingLocations: {
    experimentIds: [POLICY_IDS.keystoneForecast, POLICY_IDS.keystoneOptimized],
    // TODO(pm): hardcoded default sku/site for this page's initial view. Follow-up:
    // confirm with product whether this page should default to the current tenant's
    // top SKU/site (data-driven) instead of a fixed example, then wire that up.
    sku: 'prod_573C9FB1E788',
    site: 'North America',
  },
};

// Product-facing names for the three policies. Keyed by the canonical
// experiment_id the API returns (uppercase form). Lookup tolerates case drift.
export const POLICY_NAMES: Record<string, string> = {
  [POLICY_IDS.incumbent]: 'Incumbent',
  [POLICY_IDS.keystoneForecast]: 'Keystone Forecast, Same Constraints',
  [POLICY_IDS.keystoneOptimized]: 'Keystone Forecast & Production Optimizer',
};

const POLICY_NAMES_LC: Record<string, string> = Object.fromEntries(
  Object.entries(POLICY_NAMES).map(([k, v]) => [k.toLowerCase(), v]),
);

// Resolve a product-facing policy name. Falls back to "Incumbent" for the
// reference policy and to the raw experiment_id for any unmapped comparison.
export function policyName(experimentId: string | null | undefined, isReference = false): string {
  if (experimentId) {
    const hit = POLICY_NAMES[experimentId] ?? POLICY_NAMES_LC[experimentId.toLowerCase()];
    if (hit) return hit;
  }
  if (isReference) return 'Incumbent';
  return experimentId ?? '—';
}
